import React, { useContext, useState, useEffect, useCallback } from "react";
import {
  ExtraInformation,
  OriginType,
  DBProduct,
  ShoppingCart,
  StoreMetadata,
} from "@commons-monorepo/index";
import {
  getCartSession,
  getSessionShoppingCart,
  getShoppingCart,
  saveCartSession,
  saveSessionShoppingCart,
  saveShoppingCart,
} from "~/modules/cart/services/cart-storage";
import { fetchAbandonedCart } from "~/modules/cart/fetchers/abandoned-cart.fetcher";
import { mapProductsFromShoppingCart } from "~/modules/cart/utils/shopping-cart.utils";
import { getDbProductId, mapCalculatorPayload } from "~/modules/cart/utils";
import { useAppSearchParams } from "@commons-monorepo/index";
import { Source } from "~/modules/cart/types/checkout.types";
import { STORAGE_USER_TOKEN } from "~/config/constants/auth.constants";
import { goToCheckout as goToCheckoutFn } from "~/common/utils/navigation";
import { calculateCart } from "~/modules/cart/services/cart.service";
import { PARAM_URL_UTM_ORIGIN } from "~/constants";
import { useOutletContext } from "@remix-run/react";
import debounce from "lodash.debounce";
import Cookies from "js-cookie";

type CheckoutParams = {
  product?: DBProduct;
  skipCart?: boolean;
  extraInformation?: ExtraInformation;
};

type CartContextType = {
  cartProducts: DBProduct[];
  addProduct: (product: DBProduct) => void;
  removeProduct: (product: DBProduct) => void;
  removeAllProductsWithId: (productId: string) => void;
  manuallyChangeProduct: (product: DBProduct) => void;
  shoppingCartId: string | null;
  source: string | null;
  referrer: string | null;
  setSource: (source: string | null) => void;
  goToCheckout: (params?: CheckoutParams) => void;
  utmOrigin: string | null;
  setCouponCode: (couponCode?: string) => void;
  couponCode?: string;
  setCouponCodeEmail: (couponCodeEmail?: string) => void;
  couponCodeEmail?: string;
};

const CartContext = React.createContext<CartContextType>({
  cartProducts: [],
  addProduct: (product: DBProduct) => {},
  removeProduct: (product: DBProduct) => {},
  removeAllProductsWithId: (productId: string) => {},
  manuallyChangeProduct: (product: DBProduct) => {},
  shoppingCartId: null,
  source: null,
  utmOrigin: null,
  referrer: null,
  setSource: (source: string | null) => {},
  goToCheckout: (params?: CheckoutParams) => {},
  setCouponCode: (couponCode?: string) => {},
  couponCode: undefined,
  setCouponCodeEmail: (couponCodeEmail?: string) => {},
  couponCodeEmail: undefined,
});

export function CartContextProvider({
  children,
  storeMetadata,
  offStore,
}: {
  children: React.ReactNode;
  storeMetadata: StoreMetadata;
  offStore?: boolean;
}) {
  const data = useOutletContext<any>();
  const [cartProducts, setCartProducts] = useState<DBProduct[]>([]);
  const [shoppingCartId, setShoppingCartId] = useState<string | null>(null);
  const [source, setSource] = useState<string | null>(null);
  const [searchParams] = useAppSearchParams();
  const [couponCode, setCouponCode] = useState<string | undefined>();
  const [couponCodeEmail, setCouponCodeEmail] = useState<string | undefined>();
  const urlSource = searchParams.get("source");
  const utmSource = searchParams.get("utm_source");
  const preview = searchParams.get("preview");
  const utmOrigin = searchParams.get(PARAM_URL_UTM_ORIGIN);
  const isPrime = storeMetadata?.prime?.id !== undefined;
  const value = "google";
  const [gclid] = searchParams.getAll("gclid");
  const referrer =
    gclid && document?.referrer?.includes(value) ? "google" : null;

  useEffect(() => {
    if (preview) {
      Cookies.set("preview", "true");
    }
  }, []);

  const addProduct = (product: DBProduct) => {

    const productInCart = cartProducts.find((p) => {
      return p.dbId === getDbProductId(product);
    });
    if (productInCart) {
      productInCart.quantity += 1;
      const products = cartProducts.map((p) => {
        if (p.dbId === productInCart.dbId) return productInCart;
        return p;
      });
      setCartProducts(products);
      saveCartSession(storeMetadata.userNetwork.urlSummer, products);
    } else {
      const products = [
        ...cartProducts,
        {
          ...product,
          dbId: getDbProductId(product), //esto es necesario para crear un id relacionado con los toppings
        },
      ];
      setCartProducts(products);
      saveCartSession(storeMetadata.userNetwork.urlSummer, products);
    }
  };

  const removeProduct = (product: DBProduct) => {
    const productInCart = cartProducts.find((p) => {
      return p.dbId === getDbProductId(product);
    });
    if (productInCart) {
      if (productInCart.quantity > 1) {
        productInCart.quantity -= 1;
        const products = cartProducts.map((p) => {
          if (p.dbId === productInCart.dbId) return productInCart;
          return p;
        });
        saveCartSession(storeMetadata.userNetwork.urlSummer, products);
        setCartProducts(products);
      } else {
        const indexToRemove = cartProducts.findIndex(
          (p) => p.dbId === product.dbId,
        );
        if (indexToRemove >= 0) {
          const newCartProducts = [...cartProducts];
          newCartProducts.splice(indexToRemove, 1);
          saveCartSession(storeMetadata.userNetwork.urlSummer, newCartProducts);
          setCartProducts(newCartProducts);
        }
      }
    }
  };

  const removeAllProductsWithId = (id: string) => {
    setCartProducts(cartProducts.filter((p) => p.dbId !== id));
  };

  const manuallyChangeProduct = (product: DBProduct) => {
    const productInCart = cartProducts.find((p) => {
      return p.dbId === getDbProductId(product);
    });
    if (productInCart) {
      if (product.quantity > 0) {
        productInCart.quantity = product.quantity;
        setCartProducts(
          cartProducts.map((p) => {
            if (p.dbId === productInCart.dbId) return productInCart;
            return p;
          }),
        );
      } else {
        const indexToRemove = cartProducts.findIndex(
          (p) => p.dbId === product.id,
        );
        if (indexToRemove >= 0) {
          const newCartProducts = [...cartProducts];
          newCartProducts.splice(indexToRemove, 1);
          setCartProducts(newCartProducts);
        }
      }
    }
  };

  const handleSetShoppingCart = (
    ownerId: string,
    newShoppingCartId?: string,
  ) => {
    saveSessionShoppingCart(ownerId, newShoppingCartId);
    setShoppingCartId(newShoppingCartId || null);
  };

  const loadAbandonedCart = useCallback(
    debounce(() => {
      if (storeMetadata) {
        const sessionShoppingCart = getSessionShoppingCart(
          storeMetadata.userNetwork.urlSummer,
        );
        handleSetShoppingCart(
          storeMetadata.userNetwork.urlSummer,
          sessionShoppingCart,
        );
        getShoppingCart(storeMetadata.userNetwork.urlSummer)
          .then((newShoppingCartId) => {
            if (newShoppingCartId) {
              saveShoppingCart(storeMetadata.userNetwork.urlSummer, "");
              handleSetShoppingCart(
                storeMetadata.userNetwork.urlSummer,
                newShoppingCartId,
              );
              fetchAbandonedCart(newShoppingCartId)
                .then((response) => {
                  const shoppingProducts =
                    mapProductsFromShoppingCart(response);
                  setCartProducts(shoppingProducts);
                  saveCartSession(
                    storeMetadata.userNetwork.urlSummer,
                    shoppingProducts,
                  );
                })
                .catch(() => {
                  handleSetShoppingCart(storeMetadata.userNetwork.urlSummer);
                });
            } else {
              getCartSession(storeMetadata.userNetwork.urlSummer || "")
                .then((restoredProducts) => {
                  if (restoredProducts) setCartProducts(restoredProducts);
                })
                .catch(() => {});
            }
          })
          .catch(() => {});
      }
    }, 250),
    [storeMetadata],
  );

  useEffect(() => {
    const handlePageShow = () => {
      loadAbandonedCart();
    };
    window.addEventListener("pageshow", handlePageShow);
    handlePageShow();
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  useEffect(() => {
    if (source) {
      setSource(urlSource);
    } else if (utmSource) {
      setSource(Source.ADS);
    }
  }, [urlSource]);

  const handleSource = (newSource: string | null) => {
    setSource(newSource);
  };

  const goToCheckout = ({
    product,
    skipCart,
    extraInformation: serviceInfo,
  }: CheckoutParams = {}) => {
    const storedCoupon = localStorage.getItem("firstCoupon");
    let couponData: { coupon: string; email: string } = {
      coupon: "",
      email: "",
    };
    if (storedCoupon) couponData = JSON.parse(storedCoupon);
    if (storeMetadata && !offStore) {
      const productChat = cartProducts.find((product) => product.chatId);
      const calculatorPayload = mapCalculatorPayload({
        products: [...cartProducts, ...(product ? [product] : [])],
        catalogue_link:
          storeMetadata?.configuration?.ownerId || data?.partnerId,
        origin_type: OriginType.WebWhatsapp,
        cart_id: shoppingCartId,
        currency: data?.storeMetadata.currency,
        locale: data?.storeMetadata.locale,
        couponCode: couponData.coupon || couponCode,
        email: couponData.email || couponCodeEmail,
        chatId: productChat?.chatId || '',
      });

      const token = localStorage.getItem(STORAGE_USER_TOKEN);

      calculateCart(calculatorPayload)
        .then(async (shoppingCart: ShoppingCart) => {
          handleSetShoppingCart(storeMetadata.userNetwork.urlSummer);
          await saveCartSession(storeMetadata.userNetwork.urlSummer, []);
          await setCartProducts([]);
          await saveShoppingCart(
            storeMetadata.userNetwork.urlSummer,
            shoppingCart.additionalInformation?.cartId || "",
          );
          goToCheckoutFn(
            storeMetadata?.userNetwork?.urlSummer ||
              storeMetadata?.configuration?.ownerId,
            shoppingCart.additionalInformation?.cartId || "",
            token ? JSON.parse(token) : undefined,
            {
              isPrime,
              referrer,
              source,
              utmOrigin,
              skipCart,
              serviceInfo: serviceInfo || {},
            },
          );
        })
        .catch(() => {});
    }
  };

  return (
    <CartContext.Provider
      value={{
        cartProducts,
        addProduct,
        removeProduct,
        removeAllProductsWithId,
        manuallyChangeProduct,
        shoppingCartId,
        source,
        setSource: handleSource,
        goToCheckout,
        utmOrigin,
        referrer,
        couponCode,
        setCouponCode,
        couponCodeEmail,
        setCouponCodeEmail,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("Context must be used within a Provider");
  }
  return context;
}
